// src/store/api/userApi.ts
import type {UpdateUserDto} from "../../types/common";
import type {
  PaginatedUserResponseDto,
  UserFilterDto,
  UserResponseDto,
  UserStatsDto,
} from "../../types/users";
import {baseApi} from "./baseApi";

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get Users with filters and pagination
    getUsers: builder.query<PaginatedUserResponseDto, UserFilterDto>({
      query: (params) => ({
        url: "/users",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({id}) => ({type: "User" as const, id})),
              {type: "User", id: "LIST"},
            ]
          : [{type: "User", id: "LIST"}],
    }),

    // Get User by ID
    getUserById: builder.query<UserResponseDto, number>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{type: "User", id}],
    }),

    // Update User
    updateUser: builder.mutation<
      UserResponseDto,
      {id: number; data: UpdateUserDto}
    >({
      query: ({id, data}) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "User", id},
        {type: "User", id: "LIST"},
      ],
    }),

    // Delete User
    deleteUser: builder.mutation<{success: boolean; message: string}, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "LIST"},
      ],
    }),

    // Get User Stats
    getUserStats: builder.query<UserStatsDto, void>({
      query: () => "/users/stats",
      providesTags: ["User"],
    }),

    // Verify User Email (Admin action)
    verifyUserEmail: builder.mutation<UserResponseDto, number>({
      query: (id) => ({
        url: `/users/${id}/verify-email`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "LIST"},
      ],
    }),

    // Change User Role
    changeUserRole: builder.mutation<
      UserResponseDto,
      {id: number; role: "CUSTOMER" | "ADMIN"}
    >({
      query: ({id, role}) => ({
        url: `/users/${id}/role`,
        method: "PATCH",
        body: {role},
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "User", id},
        {type: "User", id: "LIST"},
      ],
    }),

    // Suspend User
    suspendUser: builder.mutation<{success: boolean; message: string}, number>({
      query: (id) => ({
        url: `/users/${id}/suspend`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "User", id},
        {type: "User", id: "LIST"},
      ],
    }),

    // Activate User
    activateUser: builder.mutation<{success: boolean; message: string}, number>(
      {
        query: (id) => ({
          url: `/users/${id}/activate`,
          method: "POST",
        }),
        invalidatesTags: (result, error, id) => [
          {type: "User", id},
          {type: "User", id: "LIST"},
        ],
      }
    ),

    // Bulk Delete Users
    bulkDeleteUsers: builder.mutation<
      {success: boolean; message: string; deletedCount: number},
      {userIds: number[]}
    >({
      query: (data) => ({
        url: "/users/bulk/delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["User"],
    }),

    // Export Users
    exportUsers: builder.mutation<
      {success: boolean; downloadUrl: string},
      {format: "csv" | "excel"; filters?: UserFilterDto}
    >({
      query: (data) => ({
        url: "/users/export",
        method: "POST",
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserStatsQuery,
  useVerifyUserEmailMutation,
  useChangeUserRoleMutation,
  useSuspendUserMutation,
  useActivateUserMutation,
  useBulkDeleteUsersMutation,
  useExportUsersMutation,
} = userApi;
